import axios from 'axios';
import { processTimeBlocksFromServer } from '../components/DatePicker/utils';

const API_BASE_URL = `https://acpong.llntnino.ru/`;
const API_VERSION = `/api/v1`;
export const LOGGED_OUT_EVENT = 'LOGGED_OUT_EVENT';
// Создание экземпляра axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
const VERSION_CHECK_TIMEOUT = 10 * 60 * 1000; // 10 минут в миллисекундах

async function checkBackendVersion() {
  const lastCheckTime = localStorage.getItem('lastVersionCheckTime');
  const currentTime = new Date().getTime();

  if (lastCheckTime && currentTime - lastCheckTime < VERSION_CHECK_TIMEOUT) {
    return;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/${API_VERSION}/version`);
    const latestVersion = response.data.version;
    let backendVersion = localStorage.getItem('backendVersion') || null;

    // Если версия изменилась, показываем кнопку для обновления
    if (backendVersion && backendVersion !== latestVersion) {
      window.dispatchEvent(new Event('NEW_VERSION_AVAILABLE'));
    } else {
      backendVersion = latestVersion;
      localStorage.setItem('backendVersion', latestVersion);
    }

    // Обновляем время последней проверки версии
    localStorage.setItem('lastVersionCheckTime', currentTime);
  } catch (error) {
    console.error('Error fetching backend version:', error);
  }
}

apiClient.interceptors.response.use(
  async (response) => {
    // Если ответ успешный, просто возвращаем его
    await checkBackendVersion(); // Проверяем версию при каждом успешном запросе
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Если статус ответа 401 (Unauthorized), перенаправляем на страницу логина
      localStorage.removeItem('token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('role_id');
      window.dispatchEvent(new Event(LOGGED_OUT_EVENT));
    }
    // Возвращаем ошибку для дальнейшей обработки
    return Promise.reject(error);
  },
);

export const fetchBackendVersion = async () => {
  const response = await axios.get(`${API_BASE_URL}/${API_VERSION}/version`);
  const latestVersion = response.data.version;
  return latestVersion;
};

export const fetchProfileData = async (token, notificationState, offset, placeId) => {
  try {
    const response = await apiClient.get(`/profile`, {
      params: {
        offset,
        place_id: placeId,
      },
      headers: {
        Authorization: `JWT ${token}`,
      },
    });
    const profileData = response.data;
    console.log(`Профиль пользователя:`, profileData);
    return profileData; // Возвращаем данные профиля
  } catch (error) {
    console.log(error);
    notificationState.openNotification(
      `Ошибка при получении профиля пользователя. ${error?.message}`,
      `error`,
    );
    console.error(`Ошибка при получении профиля пользователя:`, error);
    throw error;
  }
};

export const fetchTimeBlocks = async (day, place_id, notificationState) => {
  try {
    const response = await apiClient.get(
      `${API_VERSION}/timeranges?day=${day.toLocaleDateString('ru-RU')}&place_id=${place_id}`,
    );
    const timeBlocksFromServer = response.data;

    return processTimeBlocksFromServer(timeBlocksFromServer, day);
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении данных с сервера:${error}`, 'error');
    console.error('Error fetching time blocks:', error);
    return [];
  }
};

export const fetchAdminBookings = async (day, place_id, notificationState) => {
  const token = localStorage.getItem('token');

  try {
    const response = await apiClient.get(
      `${API_VERSION}/admin/books?day=${day.toLocaleDateString('ru-RU')}&place_id=${place_id}`,
      {
        headers: { Authorization: `JWT ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении данных с сервера:${error}`, 'error');
    return [];
  }
};

export const fetchPlaceData = async (notificationState, place_id) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/place`, {
      params: { place_id },
    });

    const placeData = response.data;
    console.log(`Информация о зале:`, placeData);
    return placeData;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении информации о зале`, `error`);
    console.error(`Ошибка при получении информации о зале:`, error);
    throw error;
  }
};

export const getCoaches = async (notificationState, city_id) => {
  const token = localStorage.getItem('token');

  try {
    const response = await apiClient.get(`${API_VERSION}/coaches`, {
      params: { city_id },
      headers: { Authorization: `JWT ${token}` },
    });

    const placeData = response.data;
    return placeData;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении информации о тренерах`, `error`);
    console.error(`Ошибка при получении информации о тренерах:`, error);
    throw error;
  }
};

export const getSubscriptions = async (notificationState, place_id) => {
  const token = localStorage.getItem('token');

  try {
    const response = await apiClient.get(`${API_VERSION}/subscriptions`, {
      params: { place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    const placeData = response.data;
    return placeData;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении информации об абонементах`, `error`);
    console.error(`Ошибка при получении информации об абонементах:`, error);
    throw error;
  }
};

export const createTraining = async (notificationState, data) => {
  const token = localStorage.getItem('token');

  try {
    const response = await apiClient.post(`${API_VERSION}/trainings`, data, {
      headers: { Authorization: `JWT ${token}` },
    });

    const placeData = response.data;
    return placeData;
  } catch (error) {
    notificationState.openNotification(`Ошибка при создании тренировки`, `error`);
    console.error(`Ошибка при создании тренировки:`, error);
    throw error;
  }
};

export const cancelTraining = async (token, data, notificationState) => {
  try {
    const response = await apiClient.delete(`${API_VERSION}/trainings`, {
      headers: { Authorization: `JWT ${token}` },
      data: data,
    });

    if (response.status === 200) {
      notificationState.openNotification(`Вы успешно отменили тренировку.`, `success`);
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 403:
          notificationState.openNotification(
            `Вы не можете отменить эту тренировку, она принадлежит другому тренеру.`,
            `error`,
          );
          break;
        case 404:
          notificationState.openNotification(`Такая тренировка не найдена.`, `error`);
          break;

        case 409:
          notificationState.openNotification(
            `Вы не можете отменить все тренировки разом, если на одну из них записан игрок.`,
            `error`,
          );
          break;

        default:
          notificationState.openNotification(`Ошибка при выполнении запроса. ${error}`, `error`);
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    }
  }
};

export const fetchCitiesData = async (notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/city`);

    const cityData = response.data;
    console.log(`Список городов:`, cityData);
    return cityData;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении информации о городах`, `error`);
    console.error(`Ошибка при получении информации о городах:`, error);
    throw error;
  }
};

export const fetchBookingsForDay = async (token, selectedDay, notificationState, place_id) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/profile/books`, {
      params: { day: selectedDay, place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    const DayBooks = response.data;
    console.log(`Брони дня:`, DayBooks);
    return DayBooks;
  } catch (error) {
    notificationState.openNotification(`Ошибка при получении ваших броней`, `error`);
    console.error(`Ошибка при получении ваших броней:`, error);
    throw error;
  }
};

export const fetchTrainingsData = async (place_id, day) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/trainings`, {
      params: { place_id, day },
    });

    const trainingsData = response.data;
    console.log(`Информация о тренировках:`, trainingsData);
    return trainingsData;
  } catch (error) {
    console.error(`Ошибка при получении информации о тренировках:`, error);
    throw error;
  }
};

export const patchTrainingsData = async (token, notificationState, payload) => {
  try {
    const response = await apiClient.patch(
      `${API_VERSION}/trainings`,
      {
        payload,
      },
      {
        headers: { Authorization: `JWT ${token}` },
      },
    );
    if (response.status === 200) {
      notificationState.openNotification(`Тренировка успешно изменена.`, `success`);
      const data = response.data;
      return data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 406:
          notificationState.openNotification(
            `Не удалось изменить параметры тренировки. Обновите страницу.`,
            `error`,
          );
          break;

        default:
          notificationState.openNotification(`Ошибка при выполнении запроса. ${error}`, `error`);
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    }
  }
};

export const fetchSubscriptionsData = async (place_id) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/subscriptions`, {
      params: { place_id },
    });

    const subscriptionsData = response.data;
    console.log(`Доступные подписки:`, subscriptionsData);
    return subscriptionsData;
  } catch (error) {
    console.error(`Ошибка при получении доступных подписок:`, error);
    throw error;
  }
};

export const registerTransaction = async (reservations, token, notificationState, type) => {
  try {
    const response = await apiClient.post(
      `${API_VERSION}/payment`,
      {
        data: reservations,
        type,
      },
      {
        headers: { Authorization: `JWT ${token}` },
      },
    );

    const data = response.data;
    return data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 409:
          notificationState.openNotification(
            `Упс! Кажется стол на это время уже забронирован. Обновите страницу.`,
            `error`,
          );
          break;
        case 417:
          notificationState.openNotification(
            `У вас уже есть такой абонемент <br> Ждем на тренировку!.`,
            `warning`,
          );
          break;

        default:
          notificationState.openNotification(`Ошибка при выполнении запроса. ${error}`, `error`);
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    }
  }
};

export const sendReservations = async (token, status, notificationState) => {
  try {
    const response = await apiClient.post(`${API_VERSION}/payment/resolve`, status, {
      headers: { Authorization: `JWT ${token}` },
    });

    notificationState.openNotification(`Бронь подтверджена! Ждём вас в клубе!`, `success`);
    return true; // Возвращаем true в случае успешного запроса
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification(
            `Ошибка при бронировании стола, транзакция не существует.`,
            `error`,
          );
          break;
        case 402:
          notificationState.openNotification(
            `Ошибка при бронировании стола, оплата не произошла.`,
            `error`,
          );
          break;
        case 406:
          notificationState.openNotification(
            `Ошибка при бронировании стола, сумма оплаты не соответсвует ожидаемой, обратитесь к администрации.`,
            `error`,
          );
          break;
        case 503:
          notificationState.openNotification(
            `Ошибка ответа от банка. Ваша бронь заморожена, обратитесь к администрации`,
            `error`,
          );
          break;
        default:
          notificationState.openNotification(
            `Ошибка при бронировании стола, обратитесь к администрации.`,
            `error`,
          );
      }
    }
    console.error(`Ошибка при бронировании стола:`, error);
    return false; // Возвращаем false в случае ошибки
  }
};

export const cancelBooking = async (token, bookingsToCancel, cancelForYear, notificationState) => {
  try {
    await apiClient.patch(
      `${API_VERSION}/bookings`,
      {
        bookings: bookingsToCancel,
        delete_all: cancelForYear,
      },
      {
        headers: { Authorization: `JWT ${token}` },
      },
    );

    notificationState.openNotification(`Бронь отменена.`, `success`);
  } catch (error) {
    notificationState.openNotification(`Ошибка при отмене бронирования`, `error`);
    console.error(`Ошибка при отмене бронирования:`, error);
    throw error;
  }
};

export const purchaseSubscription = async (token, purchaseData) => {
  try {
    const response = await apiClient.post(`${API_VERSION}/subscription/purchase`, purchaseData, {
      headers: { Authorization: `JWT ${token}` },
    });

    const data = response.data;
    return data;
  } catch (error) {
    console.error(`Ошибка при выполнении запроса:`, error);
    throw error;
  }
};

export const handleSubcript = async (token, payload, notificationState) => {
  try {
    const response = await apiClient.post(`${API_VERSION}/training/players`, payload, {
      headers: { Authorization: `JWT ${token}` },
    });
    if (response.status === 200) {
      notificationState.openNotification(`Вы записаны на тренировку.`, `success`);
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 409:
          notificationState.openNotification(`Вы уже записаны на эту тренировку.`, `warning`);
          break;
        case 424:
          notificationState.openNotification(`На тренировке нет мест.`, `error`);
          break;
        case 403:
          notificationState.openNotification(`У вас нет абонемента на эту тренировку.`, `error`);
          break;
        case 416:
          notificationState.openNotification(
            `Тренировка уже закончилась, вы не можете на неё записаться`,
            `error`,
          );
          break;
        default:
          notificationState.openNotification(`Непредвиденная ошибка сервера.`, `error`);
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    }
  }
};

export const unsubscribePlayer = async (token, payload, notificationState) => {
  try {
    const response = await apiClient.delete(`${API_VERSION}/training/players`, {
      headers: { Authorization: `JWT ${token}` },
      data: payload,
    });
    if (response.status === 200) {
      notificationState.openNotification(`Успешно.`, `success`);
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 403:
          notificationState.openNotification(
            `Вы не можете отменить участие этого пользователя.`,
            `error`,
          );
          break;
        default:
          notificationState.openNotification(`Непредвиденная ошибка сервера.`, `error`);
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    }
  }
};

export const getTrainingPlayers = async (token, training_id, notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/training/players`, {
      params: { training_id },
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    const status = error.response.status;
    switch (status) {
      case 401:
        notificationState.openNotification(
          `Войдите в систему, чтобы записаться на тренировку.`,
          `error`,
        );
        break;
      default:
        notificationState.openNotification(`Не удалось получить список игроков. ${error}`, `error`);
        console.log(error);
        throw error;
    }
  }
};

export const createProfile = async (name, password, smsCode, token, notificationState) => {
  try {
    const data = { name, password, smsCode };
    const response = await apiClient.post(`/profile`, data, {
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      console.log(`Регистрация успешна!`);
      notificationState.openNotification(`Регистрация успешна!`, `success`);
      return true;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 403:
          notificationState.openNotification(
            `Вы ввели неверный код или его время жизни вышло (10 минут).`,
            `error`,
          );
          break;
        case 409:
          notificationState.openNotification(
            `Пользователь с таким телефоном уже существует`,
            `error`,
          );
          break;
        case 406:
          notificationState.openNotification(`Произошла ошибка при регистрации профиля`, `error`);
          break;
        default:
          notificationState.openNotification(
            `Произошла неизвестная ошибка при регистрации.`,
            `error`,
          );
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const updateProfileName = async (payload, token, notificationState) => {
  try {
    const response = await apiClient.patch(`/profile`, payload, {
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      notificationState.openNotification(`Имя было изменено успешно!`, `success`);
      return true;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification(`Такой пользователь не найден.`, `error`);
          break;
        default:
          notificationState.openNotification(
            `Произошла неизвестная ошибка при регистрации.`,
            `error`,
          );
          console.error(`Ошибка при выполнении запроса:`, error);
          throw error;
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const resetPassword = async (password, smsCode, token, notificationState) => {
  try {
    const data = { password, smsCode };
    const response = await apiClient.post(`${API_VERSION}/reset`, data, {
      headers: {
        Authorization: `${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      notificationState.openNotification('Смена пароля прошла успешно!', 'success');
      return true;
    }
  } catch (error) {
    // Если ошибка пришла с сервера и содержит response
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 406:
          notificationState.openNotification('Произошла ошибка при смене пароля', 'error');
          break;
        case 403:
          notificationState.openNotification(
            'Вы ввели неверный код или его время жизни вышло (10 минут)',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            `Произошла неизвестная ошибка при смене пароля. Код: ${status}`,
            'error',
          );
      }
    } else {
      // Если ошибка не связана с серверным ответом (например, проблема с сетью)
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error; // Пробрасываем ошибку для возможной дальнейшей обработки
  }
};

export const sendPreauthSms = async (phone, notificationState, recaptchaValue) => {
  const data = {
    phone,
    recaptcha: recaptchaValue,
  };

  try {
    const response = await apiClient.post(`${API_VERSION}/send_sms`, data);

    // Если статус ответа 200
    if (response.status === 200) {
      notificationState.openNotification(
        'Смс отправлена на указанный номер. Введите код в соответствующее поле',
        'success',
      );
      return response.data;
    }
  } catch (error) {
    // Если ошибка пришла с сервера и содержит response
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 425:
          notificationState.openNotification(
            `Слишком быстро. Дождитесь ${error.response.data}.`,
            'error',
          );
          break;
        case 422:
          notificationState.openNotification(
            'Введенный номер телефона не похож на настоящий, попробуйте другой.',
            'error',
          );
          break;
        case 412:
          notificationState.openNotification(
            'Вы подозрительно быстро нажимаете кнопки. Гугл считает вас роботом.',
            'error',
          );
          break;
        case 409:
          notificationState.openNotification(
            'Пользователь с таким номером телефона уже существует.',
            'error',
          );
          break;
        default:
          if (status >= 500) {
            notificationState.openNotification(
              'Кажется проблема с сервером, попробуйте позже.',
              'error',
            );
          }
      }
    } else {
      // Если ошибка не связана с серверным ответом (например, проблема с сетью)
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error; // Пробрасываем ошибку для возможной дальнейшей обработки
  }
};

export const sendResetSms = async (phone, notificationState, recaptchaValue) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/reset`, {
      params: {
        phone: phone.replace(`+`, `%2B`),
        recaptcha: recaptchaValue,
      },
    });

    if (response.status === 200) {
      notificationState.openNotification(
        'Смс отправлена на указанный номер. Введите код в соответствующее поле',
        'success',
      );
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 425:
          notificationState.openNotification(
            `Слишком быстро. Дождитесь ${error.response.data}.`,
            'error',
          );
          break;
        case 422:
          notificationState.openNotification(
            'Введенный номер телефона не похож на настоящий, попробуйте другой.',
            'error',
          );
          break;
        case 412:
          notificationState.openNotification(
            'Вы подозрительно быстро нажимаете кнопки. Гугл считает вас роботом.',
            'error',
          );
          break;
        case 409:
          notificationState.openNotification(
            'Пользователь с таким номером телефона не существует.',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const openTheDoor = async (notificationState, token, place_id) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/open`, {
      params: { place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      notificationState.openNotification('Дверь была открыта', 'success');
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification('Вы не можете сейчас попасть в зал', 'error');
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const openSharedDoor = async (notificationState, pwd, place_id) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/shared/open`, {
      params: { pwd, place_id },
    });

    if (response.status === 200) {
      notificationState.openNotification('Дверь была открыта', 'success');
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification('Вы не можете сейчас попасть в зал', 'error');
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const getSharedBookLink = async (bookingId, notificationState, token) => {
  try {
    const response = await apiClient.post(
      `${API_VERSION}/shared/book`,
      { book_id: bookingId },
      {
        headers: { Authorization: `JWT ${token}` },
      },
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 403:
          notificationState.openNotification(
            'Бронь принадлежит не вам, обратитесь к администрации',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const getSharedBookInfo = async (pwd, notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/shared/book`, {
      params: { pwd },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 403:
          notificationState.openNotification(
            'Бронь принадлежит не вам, обратитесь к администрации',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const getMerchantId = async (place_id, token, notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/merchant`, {
      params: { place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification(
            'Не найден идентификатор продавца. Обратитесь к администрации',
            'error',
          );
          break;
        default:
          if (status >= 500) {
            notificationState.openNotification(
              'Кажется проблема с сервером, попробуйте позже.',
              'error',
            );
          }
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const getSchemaForPlace = async (place_id, token, notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/place/schema`, {
      params: { place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification('Схема зала не найдена', 'error');
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла ошибка при выполнении запроса к серверу.',
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const getLightsState = async (place_id, token, notificationState) => {
  try {
    const response = await apiClient.get(`${API_VERSION}/lights/state`, {
      params: { place_id },
      headers: { Authorization: `JWT ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification('Зал с таким id не найден', 'error');
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        `Ошибка при получении статуса умного света. ${error.message}`,
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const updateLightsState = async (place_id, token, notificationState, state) => {
  const data = { state, place_id };

  try {
    const response = await apiClient.post(`${API_VERSION}/lights/state`, data, {
      headers: {
        Authorization: `JWT ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification(
            'Вы не можете управлять светом на этом столе.',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        `Ошибка при обновлении статуса умного света. ${error.message}`,
        'error',
      );
    }
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
};

export const fetchBookingsFreeCancel = async (token, notificationState, bookingsIds) => {
  try {
    const { data } = await apiClient.post(`${API_VERSION}/bookings/freecancel`, bookingsIds, {
      headers: { Authorization: `JWT ${token}` },
    });
    return data;
  } catch (error) {
    if (error.response) {
      notificationState.openNotification(
        `Ошибка при получении информации о бесплатной отмене брони. ${error.message}`,
        'error',
      );
    } else {
      notificationState.openNotification('Ошибка при выполнении запроса к серверу.', 'error');
    }
    console.error('Ошибка при получении информации о бесплатной отмене брони:', error);
    throw error;
  }
};

export const getAdminUserData = async (notificationState, userLogin) => {
  const token = localStorage.getItem('token');
  const params = `username=${userLogin.replace(`+`, `%2B`)}`;

  try {
    const { data } = await apiClient.get(`${API_VERSION}/admin/user/balance?${params}`, {
      headers: { Authorization: `JWT ${token}` },
    });
    return data;
  } catch (error) {
    if (error.response) {
      notificationState.openNotification(
        `Ошибка при получении информации о пользователе. ${error.message}`,
        'error',
      );
    } else {
      notificationState.openNotification('Ошибка при выполнении запроса к серверу.', 'error');
    }
    console.error('Ошибка при получении информации о пользователе:', error);
    throw error;
  }
};

export const patchAdminUserBalance = async (notificationState, userBalanceChange) => {
  const token = localStorage.getItem('token');

  try {
    const { data } = await apiClient.patch(`${API_VERSION}/admin/user/balance`, userBalanceChange, {
      headers: { Authorization: `JWT ${token}` },
    });
    return data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 404:
          notificationState.openNotification(
            'Ошибка при попытке изменения баланса, пользователь не найден.',
            'error',
          );
          break;
        case 406:
          notificationState.openNotification(
            'Ошибка при попытке изменения баланса, не удалось записать историю изменений баланса.',
            'error',
          );
          break;
        default:
          notificationState.openNotification(
            'Кажется проблема с сервером, попробуйте позже.',
            'error',
          );
      }
    } else {
      notificationState.openNotification(
        'Произошла непредвиденная ошибка при попытке изменения баланса.',
        'error',
      );
    }

    console.error('Ошибка при изменении баланса:', error);
    throw error;
  }
};
